import { useCallback, useMemo } from 'react'
import { router } from '@inertiajs/react'
import type { ViewMode, ComparisonMode } from '@/types/calendar'

export interface CalendarState {
  month: number
  year: number
  view: ViewMode
  roomTypes: number[] | null
  ratePlan: number | null
  comparison: ComparisonMode
  calendarRoomType: number | null
}

interface CalendarNavOptions {
  /**
   * The property's backend-served canonical path (`property.basePath`). Every destination this
   * hook visits is that plus a FIXED suffix — it never assembles the identity part of a URL.
   */
  basePath: string
  /** 'prices' or 'occupancy' */
  tab: 'prices' | 'occupancy'
  /** Server-provided month */
  month: number
  /** Server-provided year */
  year: number
  /** All room type IDs (for default detection) */
  allRoomTypeIds?: number[]
  /** Default rate plan ID (server-selected lead plan) */
  defaultRatePlanId?: number
}

function parseViewMode(v: string | null): ViewMode {
  if (v === 'grid' || v === 'calendar' || v === 'timeline') return v
  return 'calendar'
}

function parseComparison(v: string | null): ComparisonMode {
  if (v === 'base_rate' || v === 'yesterday' || v === 'last_week') return v
  return 'none'
}

function parseIntArray(v: string | null): number[] | null {
  if (!v) return null
  const arr = v.split(',').map(Number).filter((n) => !isNaN(n))
  return arr.length > 0 ? arr : null
}

/**
 * Reads calendar state from URL search params and provides
 * a navigate function that preserves all state across transitions.
 */
export function useCalendarNav({
  basePath,
  tab,
  month,
  year,
  allRoomTypeIds,
  defaultRatePlanId,
}: CalendarNavOptions) {
  const state = useMemo<CalendarState>(() => {
    const params = new URLSearchParams(window.location.search)
    const rpParam = params.get('rate_plan_id')
    return {
      month,
      year,
      view: parseViewMode(params.get('view')),
      roomTypes: parseIntArray(params.get('room_types')),
      ratePlan: rpParam ? Number(rpParam) : null,
      comparison: parseComparison(params.get('comparison')),
      calendarRoomType: params.get('calendar_room_type')
        ? Number(params.get('calendar_room_type'))
        : null,
    }
  }, [month, year])

  const navigate = useCallback(
    (overrides: Partial<CalendarState> & { tab?: 'prices' | 'occupancy' } = {}) => {
      const next = { ...state, ...overrides }
      const targetTab = overrides.tab ?? tab
      // The tab is the only thing that moves; `basePath` is served, never rebuilt.
      const targetPath = `${basePath}/calendar/${targetTab}`

      const data: Record<string, string | number | number[]> = {
        month: next.month,
        year: next.year,
      }

      // View — only include if not default
      if (next.view !== 'calendar') {
        data.view = next.view
      }

      // Room types — only include if not all selected
      if (next.roomTypes && allRoomTypeIds && next.roomTypes.length < allRoomTypeIds.length) {
        data.room_types = next.roomTypes.join(',')
      }

      // Rate plan — only include if different from default
      if (next.ratePlan != null && next.ratePlan !== defaultRatePlanId) {
        data.rate_plan_id = next.ratePlan
      }

      if (next.comparison !== 'none') {
        data.comparison = next.comparison
      }

      if (next.calendarRoomType != null) {
        data.calendar_room_type = next.calendarRoomType
      }

      router.visit(targetPath, { data, preserveState: false })
    },
    [state, tab, basePath, allRoomTypeIds, defaultRatePlanId]
  )

  // Effective values with defaults applied
  const view = state.view
  const selectedRoomTypeIds = state.roomTypes ?? allRoomTypeIds ?? []
  const selectedRatePlanId = state.ratePlan ?? defaultRatePlanId ?? null
  const comparisonMode = state.comparison
  const calendarRoomType = state.calendarRoomType

  return {
    state,
    view,
    selectedRoomTypeIds,
    selectedRatePlanId,
    comparisonMode,
    calendarRoomType,
    navigate,
  }
}
